import { type PropsWithChildren, type ReactNode, useEffect, useState } from "react";
import type { CommonDialogProps, DialogButtonProps } from "widgets";
import { AlertDialogComponent, Modal } from "widgets";
import { useOverlay } from "widgets/hooks";
import { useAsyncLoading } from "widgets/hooks";

import { initializeConfirmationService } from "@/services/ConfirmationService.ts";
import { ConfirmationServiceContext } from "@/services/ConfirmationServiceContext.tsx";

export interface ConfirmationOptions {
  title: string;
  message: ReactNode;
  mainButton: Pick<DialogButtonProps, "label" | "intent" | "onPress">;
  secondaryButton?: Pick<DialogButtonProps, "label" | "intent" | "onPress">;
  onCancel?: CommonDialogProps["closeFn"];
  cancelButtonLabel?: string;
  dataE2eLabel?: string;
}

/**
 * Based on https://dev.to/dmtrkovalenko/the-neatest-way-to-handle-alert-dialogs-in-react-1aoe but
 * adapted to our codebase
 */
export const ConfirmationServiceProvider = ({ children }: PropsWithChildren) => {
  const [confirmationState, setConfirmationState] = useState<ConfirmationOptions | null>(null);

  // Initialize the global service with the state setter
  useEffect(() => {
    initializeConfirmationService(setConfirmationState);
    return () => initializeConfirmationService(null); // Cleanup on unmount
  }, []);

  const openConfirmation = (options: ConfirmationOptions) => () => {
    setConfirmationState(options);
  };

  const { isAsyncLoading: isSecondaryButtonAsyncLoading, handlePress: secondaryButtonHandlePress } = useAsyncLoading(
    confirmationState?.secondaryButton?.onPress,
  );
  const { isAsyncLoading: isMainButtonAsyncLoading, handlePress: mainButtonHandlePress } = useAsyncLoading(
    confirmationState?.mainButton?.onPress,
  );

  const { state, overlayProps } = useOverlay({
    isOpen: Boolean(confirmationState),
  });

  return (
    <>
      <ConfirmationServiceContext.Provider value={openConfirmation}>{children}</ConfirmationServiceContext.Provider>

      <Modal state={state} {...overlayProps}>
        <AlertDialogComponent
          closeFn={() => {
            // Plain function because usually we want the dialog to close right away when pressing cancel
            confirmationState?.onCancel?.();
            setConfirmationState(null);
          }}
          title={confirmationState?.title}
          cancelButtonLabel={confirmationState?.cancelButtonLabel}
          mainButton={{
            label: confirmationState?.mainButton?.label ?? "OK",
            intent: confirmationState?.mainButton?.intent,
            onPress: async (event) => {
              return await Promise.resolve(mainButtonHandlePress(event)).then(() => setConfirmationState(null));
            },
            isLoading: isMainButtonAsyncLoading,
          }}
          secondaryButton={
            confirmationState?.secondaryButton
              ? {
                  label: confirmationState.secondaryButton.label,
                  onPress: async (event) => {
                    await secondaryButtonHandlePress(event);
                    setConfirmationState(null);
                    // explicitly return the promise so that the Action Button can handle it for the
                    // displaying the loading spinner
                    return;
                  },
                  intent: confirmationState?.secondaryButton.intent,
                  isLoading: isSecondaryButtonAsyncLoading,
                }
              : undefined
          }
          data-e2e-label={confirmationState?.dataE2eLabel}
        >
          {confirmationState?.message}
        </AlertDialogComponent>
      </Modal>
    </>
  );
};
